import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { MessageService } from './message.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private defaultHandleError(error: HttpErrorResponse) {
    // console.error(error);
    let errorTxt = error.error ?? error.message;
    this.mMessageService.addMessage({ text: errorTxt, type: this.mMessageService.types.error }, false);
  }

  private setHandleError(request: Observable<any>, errorHandle?: (error: HttpErrorResponse) => void): Observable<any> {
    return request.pipe(catchError(err => {
      if (errorHandle) {
        errorHandle(err);
      } else {
        this.defaultHandleError(err);
      }
      return throwError(() => new Error(err));
    }));
  }

  constructor(private mHttp: HttpClient, private mMessageService: MessageService) { }

  public post<TRequest, TResponse>(url: string, body: TRequest, useErrorHandler = true): Observable<TResponse> {
    const request = this.mHttp.post(url, body, httpOptions) as Observable<TResponse>;

    if (useErrorHandler) {
      return this.setHandleError(request);
    }

    return request;
  }

  public put<TRequest, TResponse>(url: string, body: TRequest, useErrorHandler = true): Observable<TResponse> {
    const request = this.mHttp.put(url, body, httpOptions) as Observable<TResponse>;

    if (useErrorHandler) {
      return this.setHandleError(request);
    }

    return request;
  }

  public get<TResponse>(url: string, useErrorHandler = true): Observable<TResponse> {
    const request = this.mHttp.get(url, httpOptions) as Observable<TResponse>;

    if (useErrorHandler) {
      return this.setHandleError(request);
    }

    return request;
  }

  public getWithParams<TRequest, TResponse>(url: string, param: TRequest, useErrorHandler = true): Observable<TResponse> {

    let andSymbol = '&';

    if (url.indexOf('?') === -1) {
      andSymbol = '?';
    }

    for (let prop in param) {
      if (!param[prop]) {
        continue;
      }

      if (param[prop] instanceof Object) {
        let isSet = false;

        for (let second in param[prop]) {
          if ((param as any)[prop][second] == undefined) {
            continue;
          }

          isSet = true;

          url += andSymbol + prop + '.' + second + '=' + encodeURIComponent((param as any)[prop][second] as string);
          andSymbol = '&';

        }

        if (!isSet) {
          url += andSymbol + prop + '=';
          andSymbol = '&';
        }
      } else {
        url += andSymbol + prop + '=' + encodeURIComponent(param[prop] as string);
        andSymbol = '&';
      }
    }

    const request = this.mHttp.get(url, httpOptions) as Observable<TResponse>;

    if (useErrorHandler) {
      return this.setHandleError(request);
    }

    return request;
  }

  public delete<TResponse>(url: string, useErrorHandler = true): Observable<TResponse> {
    const request = this.mHttp.delete(url, httpOptions) as Observable<TResponse>
    if (useErrorHandler) {
      return this.setHandleError(request);
    }

    return request;
  }

  public formData<TResponse>(url: string, formData: FormData, useErrorHandler = true) {
    const request = this.mHttp.post(url, formData) as Observable<TResponse>

    if (useErrorHandler) {
      return this.setHandleError(request);
    }

    return request;
  }

  public downloadFile(id: string) {

    this.mHttp.get(
      `/api/file/${id}`,
      { responseType: 'blob', observe: 'response' }
    ).subscribe(response => {
      if (!response) {
        return;
      }

      // Create a URL for the blob
      const url = URL.createObjectURL(response.body as any);

      // Create an anchor element to "point" to it
      const anchor = document.createElement('a');
      anchor.href = url;

      // Get the suggested filename for the file from the response headers
      anchor.download = this.getFilenameFromHeaders(response?.headers) || 'file';

      // Simulate a click on our anchor element
      anchor.click();

      // Discard the object data
      URL.revokeObjectURL(url);
    });



  }

  private getFilenameFromHeaders(headers: HttpHeaders) {
    // The content-disposition header should include a suggested filename for the file
    const contentDisposition = headers.get('Content-Disposition');
    if (!contentDisposition) {
      return null;
    }

    const leadIn = 'filename=';
    const start = contentDisposition.search(leadIn);
    const end = contentDisposition.indexOf(';', start);
    return contentDisposition.substring(start + leadIn.length, end);
  }
}

